import { type UseStore } from 'idb-keyval';

// Automatic recovery when the persisted-query IndexedDB store disappears at runtime —
// a developer clearing state from the devtools console, or the browser evicting it under
// storage pressure.
//
// The failure chain we are escaping (idb-keyval@6.2.1, dist/index.js):
//   1. `defaultGetStore()` memoizes its store function at MODULE scope:
//        let defaultGetStoreFunc;
//        if (!defaultGetStoreFunc) defaultGetStoreFunc = createStore('keyval-store', 'keyval');
//   2. Inside `createStore`, `indexedDB.open()` is promisified ONCE — `dbp` resolves to a
//      single IDBDatabase handle that every later call reuses.
//   3. Delete the object store (or the whole DB) and that handle is permanently stale:
//        - object store deleted        -> db.transaction('keyval', ...) throws NotFoundError
//        - whole DB deleted            -> handle closes, throws InvalidStateError
//   4. Because the store function is memoized, nothing ever re-opens. get/set/del all
//      default to it, so all three break together and STAY broken until a full reload.
//
// Why that surfaced as console spam: @tanstack/query-persist-client-core's
// `persistQueryClientSave` has no try/catch and is called fire-and-forget from the query AND
// mutation cache subscribers, discarding the returned promise. A rejecting `persistClient`
// therefore produces one unhandled rejection per cacheable cache event. Restore is guarded
// upstream; save is not — which is why recovery has to live on our side of the seam.
//
// Recovery uses TWO triggers, because they cover disjoint cases:
//   a. `versionchange`/`close` listeners — catch `deleteDatabase` and eviction proactively,
//      so the next write succeeds on its first attempt. Closing on `versionchange` is
//      load-bearing, not an optimization: an open handle leaves `deleteDatabase` blocked
//      forever, so the devtools call never resolves and the DB wedges.
//   b. Catching NotFoundError/InvalidStateError — the backstop, and the ONLY thing that sees
//      an object-store-only deletion (the database still exists, so no DB-level event fires,
//      and `indexedDB.databases()` would report it healthy).

export const DB_NAME = 'keyval-store';
export const STORE_NAME = 'keyval';

let store: UseStore | undefined;

/**
 * The two errors a vanished store throws. Anything else (QuotaExceededError, a genuine bug)
 * is not retried — reopening would not fix it.
 */
export const isStoreGoneError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('name' in error)) return false;
  const { name } = error;
  return name === 'NotFoundError' || name === 'InvalidStateError';
};

const promisifyOpen = (request: IDBOpenDBRequest): Promise<IDBDatabase> =>
  new Promise<IDBDatabase>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });

/**
 * Open the DB and GUARANTEE the object store exists.
 */
const openDbWithStore = async (): Promise<IDBDatabase> => {
  let db = await promisifyOpen(indexedDB.open(DB_NAME));

  if (!db.objectStoreNames.contains(STORE_NAME)) {
    const nextVersion = db.version + 1;
    db.close();
    const upgrade = indexedDB.open(DB_NAME, nextVersion);
    upgrade.onupgradeneeded = () => upgrade.result.createObjectStore(STORE_NAME);
    db = await promisifyOpen(upgrade);
  }

  return db;
};

const openStore = (): UseStore => {
  // One shared open per store function, but re-creatable
  const dbp = openDbWithStore();

  void dbp
    .then(db => {
      db.addEventListener('versionchange', () => {
        // MUST close, or the deletion request that triggered this stays `blocked` forever.
        db.close();
        if (store === next) store = undefined;
      });

      db.addEventListener('close', () => {
        if (store === next) store = undefined;
      });
    })
    .catch(() => {
      // open() failed outright (private mode, blocked, quota)
      if (store === next) store = undefined;
    });

  const next: UseStore = (txMode, callback) =>
    dbp.then(db => callback(db.transaction(STORE_NAME, txMode).objectStore(STORE_NAME)));

  return next;
};

const getStore = (): UseStore => {
  if (!store) store = openStore();
  return store;
};

export const resetStore = (): void => {
  store = undefined;
};

/**
 * Run an idb-keyval operation against our store, repairing a vanished store once.
 */
export const withStore = async <T>(op: (usableStore: UseStore) => Promise<T>): Promise<T> => {
  try {
    return await op(getStore());
  } catch (error) {
    if (!isStoreGoneError(error)) throw error;
    resetStore();
    return await op(getStore());
  }
};
